import { useEffect, useState, useMemo } from "react";
import Grid from "@mui/material/Grid";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "../../../examples/Tables/DataTable";
import data from "../../dashboard/components/Projects/data";
import useApi from "../../../components/Utils/api";
import toast from "utils/toast";
import { DateRangePicker } from "rsuite";
import { afterToday } from "rsuite/cjs/DateRangePicker/disabledDateUtils";
import reportsLineChartData from "../../dashboard/data/reportsLineChartData";
import Papa from 'papaparse';

const getTxnMethod = (entrMode) => {
  if (!entrMode) return "Other";
  if (entrMode.startsWith("05")) return "Contact";
  if (entrMode.startsWith("07")) return "Contactless";
  if (entrMode.startsWith("02")) return "Mag Stripe";
  if (entrMode.startsWith("01")) return "Manual";
  return "Other";
};

function PreAuthTransactionsReport() {
  const { preAuthTransactionsReportsColumns } = data();
  const { predefinedRanges } = reportsLineChartData;
  const [transactions, setTransactions] = useState([]);
  const [txnMethodFilter, setTxnMethodFilter] = useState("All");
  const [statusFilter, setStatusFilter] = useState("All");

  const filteredTransactions = useMemo(() => {
    return transactions.filter(row => {
      const method = getTxnMethod(row.entrMode);
      const isApproved = row.responseCode === '000' || row.responseCode === '00';
      if (txnMethodFilter !== "All" && method !== txnMethodFilter) return false;
      if (statusFilter === "Success" && !isApproved) return false;
      if (statusFilter === "Failed" && isApproved) return false;
      return true;
    });
  }, [transactions, txnMethodFilter, statusFilter]);

  const getTodayDateAtMidnight = () => {
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    return new Date(now.getTime() - now.getTimezoneOffset() * 60000);
  };

  const [lastMonthDateRange, setLastMonthDateRange] = useState([
    getTodayDateAtMidnight().toISOString().slice(0, 10),
    getTodayDateAtMidnight().toISOString().slice(0, 10)
  ]);

  const api = useApi();

  const formatDate = (date) => {
    if (!(date instanceof Date) || isNaN(date.getTime())) return '';
    const localDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
    return localDate.toISOString().slice(0, 10);
  };

  const getTransactions = (dateRange) => {
    if (!dateRange || dateRange.length !== 2) return;
    const data = {
      fromDate: formatDate(new Date(dateRange[0])),
      toDate: formatDate(new Date(dateRange[1]))
    };
    api.post("reports/preAuthTransactionsReport", data)
      .then((response) => {
        if (response.data.statusCodeValue === 200) {
          setTransactions(response.data.body);
        } else {
          setTransactions([]);
          const toastId = toast.error("No Record Found", { autoClose: false });
          setTimeout(() => toast.dismiss(toastId), 1000);
        }
      })
      .catch((error) => {
        if (error.response && error.response.status === 403) {
          toast.error("You do not have permission to perform this action.");
          return;
        }
        setTransactions([]);
        const toastId = toast.error("No Record Found", { autoClose: false });
        setTimeout(() => toast.dismiss(toastId), 1000);
      });
  };

  const generateCSV = () => {
    if (transactions.length === 0) return '';
    const filteredColumns = preAuthTransactionsReportsColumns.filter(col =>
      col.Header !== "Action" && col.Header !== "Settlement Allowed"
    );
    const headers = filteredColumns.map(col => col.Header);
    const rows = transactions.map(transaction =>
      filteredColumns.map(col => {
        const accessor = col.accessor;
        return typeof accessor === 'function' ? accessor(transaction) : transaction[accessor];
      })
    );
    return Papa.unparse([headers, ...rows], { quotes: true });
  };

  const handleDownloadCSV = () => {
    if (transactions.length === 0) {
      const toastId = toast.error("No records found to download", { autoClose: false });
      setTimeout(() => toast.dismiss(toastId), 1000);
      return;
    }
    const csvData = generateCSV();
    const dateTimeString = `${new Date().toISOString().slice(0, 10)}_${new Date().toLocaleTimeString().replace(/:/g, '-')}`;
    const blob = new Blob([csvData], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `PreAuthTransactions_${dateTimeString}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  useEffect(() => {
    getTransactions(lastMonthDateRange);
  }, [lastMonthDateRange]);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox mt={6} mb={3}>
        <Grid container spacing={3} justifyContent="center">
          <Grid item xs={12} lg={12}>
            <MDBox display="flex" alignItems="center" justifyContent="space-between" gap={2} mb={2} flexWrap="wrap">
              <MDBox display="flex" alignItems="center" gap={2} flexWrap="wrap">
                <FormControl
                  size="small"
                  sx={{
                    minWidth: 180,
                    "& .MuiOutlinedInput-root": { height: "36.5px" },
                    "& .MuiInputLabel-root": { top: "-4px" },
                    "& .MuiInputLabel-shrink": { top: 0 },
                  }}
                >
                  <InputLabel>Transaction Method</InputLabel>
                  <Select
                    value={txnMethodFilter}
                    label="Transaction Method"
                    onChange={e => setTxnMethodFilter(e.target.value)}
                  >
                    <MenuItem value="All">All</MenuItem>
                    <MenuItem value="Contact">Contact</MenuItem>
                    <MenuItem value="Contactless">Contactless</MenuItem>
                    <MenuItem value="Mag Stripe">Mag Stripe</MenuItem>
                    <MenuItem value="Manual">Manual</MenuItem>
                    <MenuItem value="Other">Other</MenuItem>
                  </Select>
                </FormControl>

                <FormControl
                  size="small"
                  sx={{
                    minWidth: 160,
                    "& .MuiOutlinedInput-root": { height: "36.5px" },
                    "& .MuiInputLabel-root": { top: "-4px" },
                    "& .MuiInputLabel-shrink": { top: 0 },
                  }}
                >
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={statusFilter}
                    label="Status"
                    onChange={e => setStatusFilter(e.target.value)}
                  >
                    <MenuItem value="All">All</MenuItem>
                    <MenuItem value="Success">Approved</MenuItem>
                    <MenuItem value="Failed">Declined</MenuItem>
                  </Select>
                </FormControl>

                <DateRangePicker
                  shouldDisableDate={afterToday()}
                  ranges={predefinedRanges}
                  onChange={(dateRange) => {
                    if (dateRange.length === 2) {
                      setLastMonthDateRange(dateRange);
                    }
                  }}
                  format="dd MMMM yyyy"
                  value={
                    lastMonthDateRange.length === 2
                      ? [new Date(lastMonthDateRange[0]), new Date(lastMonthDateRange[1])]
                      : []
                  }
                />
              </MDBox>

              <MDButton variant="gradient" onClick={handleDownloadCSV} color="info">
                Export
              </MDButton>
            </MDBox>

            <DataTable
              table={{ columns: preAuthTransactionsReportsColumns, rows: filteredTransactions }}
              showTotalEntries={true}
              isSorted={true}
              noEndBorder
              entriesPerPage={true}
              canSearch={true}
            />
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default PreAuthTransactionsReport;
